import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ShieldCheck } from 'lucide-react';
import { safeStorage, pruneStorage } from '../../utils/storage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  attemptedRecovery: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    attemptedRecovery: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, attemptedRecovery: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React Error caught by ErrorBoundary:', error, errorInfo);
    
    // Auto-remedy for QuotaExceededError
    if (error?.message?.toLowerCase().includes('quota') || error?.name === 'QuotaExceededError') {
      try {
        pruneStorage();
      } catch (e) {
        console.warn('Auto storage prune warning in ErrorBoundary:', e);
      }
    }
  }

  private handleManualReset = () => {
    try {
      safeStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn("Storage reset warning:", e);
    }
    window.location.href = '/';
  };

  private handleQuickFix = () => {
    try {
      pruneStorage();
    } catch (e) {
      console.warn('Storage quick fix warning:', e);
    }
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const isQuotaError = this.state.error?.message?.toLowerCase().includes('quota') || this.state.error?.name === 'QuotaExceededError';

      return this.props.fallback || (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-500/10 p-12 text-center border border-slate-100">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
              <AlertTriangle className="text-amber-500" size={40} />
            </div>
            
            <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-4">
              {isQuotaError ? 'Storage Memory Cleared' : 'Stability Intervention'}
            </h1>
            
            <p className="text-slate-500 text-sm mb-8 leading-relaxed">
              {isQuotaError
                ? 'Browser local storage reached capacity. Non-essential cache has been pruned to restore full system speed and responsiveness.'
                : "The anti-error software detected a disruption in the application flow. We've prepared a diagnostic recovery path for you."}
            </p>

            <div className="space-y-3">
              <button
                onClick={this.handleQuickFix}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3"
              >
                <RefreshCw size={18} />
                {isQuotaError ? 'Reload & Resume' : 'Attempt Quick Fix'}
              </button>
              
              <button
                onClick={this.handleManualReset}
                className="w-full py-4 border border-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-3"
              >
                <Home size={18} />
                Full System Reset
              </button>
            </div>

            {this.state.error && (
              <div className="mt-8 p-4 bg-slate-50 rounded-xl text-left">
                <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1">Error Signature</p>
                <p className="text-[11px] font-mono text-slate-600 break-all">{this.state.error.message}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
